import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Text, Checkbox } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useApi } from '../../../../context/ApiProvider';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';
import { StickyScrollableTableContainer } from '../../../DataTable/StickyScrollableTableContainer';

type ArchivedTeacherInterface = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
};

const ArchiveTeachers = () => {
  const { apiTenantSpecialConsumerController, apiTenantUserController } = useApi();
  const { id: tenantId } = useParams();
  const [teachers, setTeachers] = useState<ArchivedTeacherInterface[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<number[]>([]);
  const [isUnarchiving, setIsUnarchiving] = useState<boolean>(false);
  const columnHelper = createColumnHelper<ArchivedTeacherInterface>();

  const handleCheckboxChange = (teacherId: number) => {
    setSelectedTeacherIds((prevSelected) =>
      prevSelected.includes(teacherId)
        ? prevSelected.filter((id) => id !== teacherId)
        : [...prevSelected, teacherId]
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedTeacherIds(teachers.map((teacher) => teacher.id));
    } else {
      setSelectedTeacherIds([]);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Checkbox
          colorScheme="customOrange"
          isChecked={selectedTeacherIds.length === teachers.length && teachers.length > 0}
          isDisabled={teachers.length === 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          colorScheme="customOrange"
          isChecked={selectedTeacherIds.includes(row.original.id)}
          onChange={() => handleCheckboxChange(row.original.id)}
        />
      ),
      size: 40
    }),
    columnHelper.accessor('lastName', {
      cell: (info) => {
        const { lastName, firstName } = info.row.original;
        return (
          <p className="text-sm font-medium text-grayLight-900">
            {lastName} {firstName}
          </p>
        );
      },
      header: 'Nazwisko i imię'
    }),
    columnHelper.accessor('email', {
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900">{info.getValue() || '-'}</p>
      ),
      header: 'E-mail'
    })
  ];

  const fetchArchivedTeacher = async () => {
    setIsLoading(true);
    try {
      const response = await apiTenantSpecialConsumerController('archive-teachers').get(
        `/${tenantId}`
      );
      setTeachers(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const unArchivedTeacher = async (consumerId: number) => {
    try {
      await apiTenantUserController('unarchive').post(`/${consumerId}`);
    } catch (error) {
      console.error(`Failed to unarchive child with ID ${consumerId}`, error);
    }
  };

  const handleUnarchiveSelectedTeacher = async () => {
    setIsUnarchiving(true);
    try {
      for (const teacherId of selectedTeacherIds) {
        await unArchivedTeacher(teacherId);
      }
      await fetchArchivedTeacher();
      setSelectedTeacherIds([]);
    } catch (error) {
      console.error('Error during batch unarchive', error);
    } finally {
      setIsUnarchiving(false);
    }
  };

  useEffect(() => {
    if (tenantId === '0' || tenantId === 'undefined') return;
    fetchArchivedTeacher();
  }, [tenantId]);

  return (
    <section className="ChildrenTable p-spacing-xl w-full">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text fontSize="xl" fontWeight="semibold" color="gray.900">
            Archiwum nauczycieli
          </Text>
          <Text fontSize="sm" color="gray.600">
            Lista zarchiwizowanych nauczycieli. Po przywróceniu należy ustawić właściwy email.
          </Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <CustomChakraButton
            size="md"
            hierarchy="primary"
            iconPosition="left"
            icon="rollBack"
            buttonProps={{
              onClick: handleUnarchiveSelectedTeacher,
              disabled: selectedTeacherIds.length === 0 || isUnarchiving
            }}>
            Przywróć nauczyciela
          </CustomChakraButton>
        </div>
      </Box>

      <StickyScrollableTableContainer
        data={teachers}
        columns={columns}
        isLoading={isLoading}
        enableSearch={true}
        searchPlaceholder="Szukaj nauczyciela..."
      />
    </section>
  );
};

export default ArchiveTeachers;
